/**
 * Class description
 *
 * Created: 2019-08-13
 * @author jonas
 */

import {BasicBitmapCountUp} from "../display/BasicBitmapCountUp";
import {BookOfShadowsTextStyles} from "../config/BookOfShadowsTextStyles";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {Ease, Linear, TimelineLite, TweenLite} from "gsap";
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {BookOfShadowsLayerConfig} from "../config/BookOfShadowsLayerConfig";
import {SoundManager} from "../sound/SoundManager";
import {BookOfShadowsSoundConfig} from "../BookOfShadowsSoundConfig";
import {BookOfShadowsGameConfig} from "../BookOfShadowsGameConfig";

export class CountUpView extends BaseView {
    private _layer: ScalableLayer;
    private _bitmapText: BasicBitmapCountUp;
    private _soundPlaying: boolean = false;

    private _normalWinRotateTween: TweenLite | null;
    private _ended: boolean = false;

    /**
     * @constructs
     */
    constructor() {
        super();
        this._layer = StageManager.getLayer(BookOfShadowsLayerConfig.WIN_COUNT_UP.name);
    }

    /**
     * @description Called from BookOfShadowsScatterWinController's abort method.
     * #276 fix
     */
    public soundPlayingInScatterWinAbort(): void {
        this._soundPlaying = true;
    }

    /**
     * @description This method calculates the text scale.
     * @param from
     * @param to
     */
    public calculateTextScale(from: number, to: number) {
        this._bitmapText.preCalculateScale(from, to);
    }

    /**
     * @description This method is used for the count up animation.
     * @param from
     * @param to
     * @param duration
     * @param isBigWin
     * @param ease
     */
    public getWinStepAnimation(from: number, to: number, duration: number, isBigWin: boolean, ease: Ease = Linear.easeNone): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        tl.add(() => {
            this.positionBitMapTextItem(isBigWin);
            this._layer.addChild(this);
            this.startCountUpSound(isBigWin, duration);
        });
        tl.add(this._bitmapText.getCountAnimation(from, to, duration, ease));

        return tl;
    }

    /**
     * @inheritDoc
     * @param value
     */
    public abort(value: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        tl.add(() => this._bitmapText.stopAt(value));
        return tl;
    }

    /**
     * @description This method gives us the end animation of count up.
     * @param value
     * @param displayDuration
     * @param fadeDuration
     * @param isBigWin
     */
    public end(value: number, displayDuration: number, fadeDuration: number, isBigWin: boolean): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this._ended = true;
        tl.add(() => {
            // making sure this.ended is true, in case any race condition modified it.
            this._ended = true;
            this._normalWinRotateTween = null;
            this._bitmapText.rotation = 0;

            this.positionBitMapTextItem(isBigWin);
            this._layer.addChild(this);
            this.stopCountUpSound(isBigWin);
            this._bitmapText.stopAt(value);
            SlotGame.winFieldController.showWinField();
        });
        tl.add(() => this.fadeAndRemove(fadeDuration), displayDuration);
        return tl;
    }

    /**
     * @description To play the scale animation.
     * @param from
     * @param to
     */
    public playScaleAnimation(from: number, to: number): void {
        const anim: TweenLite = TweenLite.fromTo(this._bitmapText.scale, (from === 0) ? 0.2 : 0.1, {x: from, y: from}, {
            x: to, y: to, onComplete: () => {
                if (from !== 0) {
                    anim.reverse();
                }
            }
        });
    }

    /**
     * @description To trigger animation on count up item during normal win.
     */
    public playAnimOnNormalWin(): void {
        TweenLite.fromTo(this._bitmapText.scale, 0.1, {x: 0, y: 0}, {
            x: 1.2, y: 1.2, onComplete: () => {
                TweenLite.to(this._bitmapText.scale, 0.1, {x: 1, y: 1});
            }
        });

        this.playRotate();
    }

    /**
     * @inheritDoc
     */
    protected initAnimations(): void {
        this._bitmapText = new BasicBitmapCountUp(BookOfShadowsTextStyles.WIN_COUNT_UP, GameConfig.instance.REEL_AREA_WIDTH, new PIXI.Point(0.5, 0.5));
        this.addChild(this._bitmapText);
    }

    /**
     * @description This method removes the count up item using the fade out effect.
     * @param fadeDuration
     */
    private fadeAndRemove(fadeDuration: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        tl.add(new TweenLite(this, fadeDuration, {alpha: 0, ease: Linear.easeNone}));
        tl.add(() => {
            this._layer.removeChild(this);
            this.alpha = 1;
        });
        return tl;
    }

    /**
     * @description To start the count up sound.
     * @param isBigWin
     * @param duration
     */
    private startCountUpSound(isBigWin: boolean, duration: number) {
        if (!this._soundPlaying) {
            if (isBigWin) {
                SoundManager.fadeAmbienceLoop(0, 200);
                SoundManager.playBigWinLoop();

                SoundManager.playBigWinStart();
                SoundManager.playBigWinCountUp();
            } else if (duration > 0) {
                SoundManager.playWinCountUp();
            }
            this._soundPlaying = true;
        }
    }

    /**
     * @description To stop the count up sound.
     * @param isBigWin
     */
    private stopCountUpSound(isBigWin: boolean) {
        if (this._soundPlaying) {
            if (!isBigWin) {
                // #331
                if (!BookOfShadowsGameConfig.instance.WIN_BELOW_STAKE) {
                    SoundManager.playWinCountUpEnd();
                }
            } else {
                SoundManager.fadeAmbienceLoop(1, 200);
            }

            SoundManager.stopSoundEffect(BookOfShadowsSoundConfig.instance.WIN_COUNT_UP);

            SoundManager.stopSoundEffect(BookOfShadowsSoundConfig.instance.BIG_WIN_LOOP);

            this._soundPlaying = false;
        }
    }

    /**
     * @description To position the count up item depending upon if it is bigWin or not.
     * @param isBigWin
     */
    private positionBitMapTextItem(isBigWin: boolean): void {
        if (isBigWin) {
            this._bitmapText.position.set(GameConfig.instance.REEL_AREA_WIDTH * 0.5 + GameConfig.instance.REEL_AREA_POS_X, GameConfig.instance.REEL_AREA_HEIGHT * 0.7 + GameConfig.instance.REEL_AREA_POS_Y);
        } else {
            this._bitmapText.position.set(GameConfig.instance.REEL_AREA_WIDTH * 0.5 + GameConfig.instance.REEL_AREA_POS_X, GameConfig.instance.REEL_AREA_HEIGHT * 0.5 + GameConfig.instance.REEL_AREA_POS_Y);
        }
    }

    /**
     * @description To play the rotate animation on the count up item.
     */
    private playRotate(): void {
        this._bitmapText.rotation = 0;
        this._normalWinRotateTween = TweenLite.to(this._bitmapText, 0.2, {
            rotation: 0.05, onComplete: () => {
                this._normalWinRotateTween = TweenLite.to(this._bitmapText, 0.2, {
                    rotation: 0,
                    onComplete: () => {
                        if (this._ended) {
                            this._normalWinRotateTween = null;
                            this._ended = false;
                        } else {
                            this.playRotate();
                        }
                    }
                });
            }
        });
    }
}